import '@/App.css';
import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import { captureUtmParams, trackFunnelEvent } from '@/utils/tracking';
import { getDeviceBucket } from '@/utils/deviceBucket';
import { bootstrapConsent } from '@/utils/cookieConsent';
import HomePage from '@/pages/HomePage';
import QuizPage from '@/pages/QuizPage';
import EnergyControlIntakePage from '@/pages/EnergyControlIntakePage';
import EnergyControlReportPage from '@/pages/EnergyControlReportPage';
import EnergyControlStarterKitPage from '@/pages/EnergyControlStarterKitPage';
import ReportPage from '@/pages/ReportPage';
import PaymentSuccessPage from '@/pages/PaymentSuccessPage';
import PaymentCancelPage from '@/pages/PaymentCancelPage';
import MonitoringSuccessPage from '@/pages/MonitoringSuccessPage';
import MonitoringCancelPage from '@/pages/MonitoringCancelPage';
import AdminPage from '@/pages/AdminPage';
import AdminLoginPage from '@/pages/AdminLoginPage';
import AdminInstallPage from '@/pages/AdminInstallPage';
import AdminDeviceHealthPage from '@/pages/AdminDeviceHealthPage';
import AdminGrowthEnginePage from '@/pages/AdminGrowthEnginePage';
import AdminMailHealthPage from '@/pages/AdminMailHealthPage';
import AdminRecoverySetPasswordPage from '@/pages/AdminRecoverySetPasswordPage';
import DeviceDiscoveryPage from '@/pages/DeviceDiscoveryPage';
import ResetPasswordPage from '@/pages/ResetPasswordPage';
import ForgotPasswordPage from '@/pages/ForgotPasswordPage';
import PrivacyPolicyPage from '@/pages/PrivacyPolicyPage';
import TermsPage from '@/pages/TermsPage';
import ContactPage from '@/pages/ContactPage';
import MyDevicesPage from '@/pages/MyDevicesPage';
import CustomerDeviceOnboardingPage from '@/pages/CustomerDeviceOnboardingPage';
import MijnPowersurePage, { PilotLandingPage } from '@/pages/MijnPowersurePage';
import { CustomerLoginPage, CustomerSetPasswordPage, CustomerForgotPasswordPage, CustomerResendAccessPage } from '@/pages/CustomerAuthPages';
import NotFoundPage from '@/pages/NotFoundPage';
import CookieConsentBanner from '@/components/legal/CookieConsentBanner';

function App() {
  useEffect(() => {
    captureUtmParams();
    // GO4.2-followup: include device_type bucket on page_view so the admin
    // funnel analytics can split mobile vs desktop end-to-end (other events
    // already carry this field).
    trackFunnelEvent('page_view', { meta: { device_type: getDeviceBucket() } });
    bootstrapConsent();
  }, []);

  return (
    <div className="App">
      <Toaster position="top-right" richColors />
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/quiz" element={<QuizPage />} />
          <Route path="/energy-control-intake" element={<EnergyControlIntakePage />} />
          <Route path="/energy-control-report/:intakeId" element={<EnergyControlReportPage />} />
          <Route path="/energy-control-starter-kit/:intakeId" element={<EnergyControlStarterKitPage />} />
          <Route path="/report/:reportId" element={<ReportPage />} />
          <Route path="/payment/success" element={<PaymentSuccessPage />} />
          <Route path="/payment/cancel" element={<PaymentCancelPage />} />
          <Route path="/monitoring/success" element={<MonitoringSuccessPage />} />
          <Route path="/monitoring/cancel" element={<MonitoringCancelPage />} />
          <Route path="/admin/login" element={<AdminLoginPage />} />
          <Route path="/admin/install" element={<AdminInstallPage />} />
          <Route path="/admin/devices/health" element={<AdminDeviceHealthPage />} />
          <Route path="/admin/growth/engine" element={<AdminGrowthEnginePage />} />
          <Route path="/admin/mail/health" element={<AdminMailHealthPage />} />
          <Route path="/admin/recovery/set-password" element={<AdminRecoverySetPasswordPage />} />
          <Route path="/device-discovery" element={<DeviceDiscoveryPage />} />
          <Route path="/admin" element={<AdminPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/my-devices" element={<MyDevicesPage />} />
          <Route path="/onboarding/devices" element={<CustomerDeviceOnboardingPage />} />
          <Route path="/app" element={<MijnPowersurePage />} />
          <Route path="/app/pilot/:code" element={<PilotLandingPage />} />
          {/* Iter 78 — Customer auth */}
          <Route path="/customer/login" element={<CustomerLoginPage />} />
          <Route path="/customer/set-password" element={<CustomerSetPasswordPage />} />
          <Route path="/customer/forgot-password" element={<CustomerForgotPasswordPage />} />
          <Route path="/customer/resend-access" element={<CustomerResendAccessPage />} />
          {/* Aliases — bezoekers gokken vaak de kortere URL */}
          <Route path="/energy-control" element={<Navigate to="/energy-control-intake" replace />} />
          <Route path="/dashboard" element={<Navigate to="/my-devices" replace />} />
          {/* Catch-all 404 — geen wit scherm meer bij onbekende paden */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
        <CookieConsentBanner />
      </BrowserRouter>
    </div>
  );
}

export default App;
